import { Stack, Typography } from '@mui/material';
import { KeyboardEvent, useState } from 'react';

import { KeyboardShortcutKey, ModifierKey } from '@/components/DiagramBar';
import { TextInput } from '@/components/form/baseInputs/TextInput/TextInput';
import { ChatMarkdown } from '@/modules/common/Chat/components/ChatMarkdown';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { HouseholdChatDictationControl } from '@/modules/household/chat/components/HouseholdChatDictationControl';
import { getInteractiveMessageInputSx } from '@/modules/household/chat/components/messages/interactive/interactiveMessage.styles';
import { getInteractiveMessageResponse } from '@/modules/household/chat/components/messages/interactive/interactiveMessage.utils';
import {
  InteractiveMessageActionConfig,
  InteractiveMessageCard,
} from '@/modules/household/chat/components/messages/interactive/InteractiveMessageCard';
import {
  HouseholdChatMessage,
  useHouseholdChatContext,
} from '@/modules/household/chat/contexts/householdChat.context';
import type { DictationState } from '@/modules/household/chat/hooks/useHouseholdChatDictation';
import { COLORS } from '@/styles/tokens/colors';
import { MessageResponseAction, MessageStatus } from '@/types/schema';

interface FreeFormQuestionMessageRendererProps {
  message: HouseholdChatMessage;
}

const FREE_FORM_QUESTION_ACTIONS: Record<
  MessageResponseAction,
  InteractiveMessageActionConfig
> = {
  [MessageResponseAction.Dismiss]: { label: 'Cancel', variant: 'secondary' },
  [MessageResponseAction.Submit]: {
    label: 'Submit',
    tooltip: (
      <Stack direction="row" alignItems="center" gap={0.5}>
        Submit with <ModifierKey /> +{' '}
        <KeyboardShortcutKey>Enter</KeyboardShortcutKey>
      </Stack>
    ),
    variant: 'primary',
  },
};

const VALIDATION_FREE_FORM_QUESTION_ACTIONS = {
  ...FREE_FORM_QUESTION_ACTIONS,
  [MessageResponseAction.Dismiss]: { label: 'Skip', variant: 'secondary' },
} satisfies Record<MessageResponseAction, InteractiveMessageActionConfig>;

const FREE_FORM_QUESTION_RESPONDED_VERBS: Record<
  MessageResponseAction,
  string
> = {
  [MessageResponseAction.Submit]: 'answered',
  [MessageResponseAction.Dismiss]: 'skipped this question',
};

/** Renders a free-form question with its answer field and response actions. */
export function FreeFormQuestionMessageRenderer({
  message,
}: FreeFormQuestionMessageRendererProps) {
  const [answer, setAnswer] = useState('');
  const [dictationState, setDictationState] = useState<DictationState>('idle');
  const audioTranscriptionEnabled = useFeatureFlag(
    'household_chat_audio_transcription'
  );
  const {
    isValidationQuestionsConversation,
    respondToMessage,
    respondingToMessage,
  } = useHouseholdChatContext();
  const questionContent = message.freeFormQuestionContent;
  const response = getInteractiveMessageResponse(message);
  const trimmedAnswer = answer.trim();
  const isSubmitting = respondingToMessage?.messageId === message.id;
  const dictationBusy = dictationState !== 'idle';

  if (!questionContent) {
    // The typed question is decrypted server-side. If it cannot be resolved,
    // preserve the persisted plain-text question instead of rendering an
    // empty card.
    return <ChatMarkdown markdown={message.content} />;
  }

  const handleRespond = (action: MessageResponseAction) => {
    const input = {
      messageID: message.id,
      responseAction: action,
    };
    if (action === MessageResponseAction.Submit) {
      void respondToMessage({ ...input, freeFormText: trimmedAnswer });
      return;
    }
    void respondToMessage(input);
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    // Command+Enter on macOS or Ctrl+Enter on Windows/Linux submits the response.
    if (event.key !== 'Enter' || (!event.metaKey && !event.ctrlKey)) {
      return;
    }

    event.preventDefault();
    if (trimmedAnswer) {
      handleRespond(MessageResponseAction.Submit);
    }
  };

  const appendTranscript = (transcript: string) => {
    if (!transcript) {
      return;
    }
    setAnswer((currentAnswer) => {
      const separator =
        currentAnswer && !currentAnswer.endsWith(' ') ? ' ' : '';
      return `${currentAnswer}${separator}${transcript}`;
    });
  };

  return (
    <InteractiveMessageCard
      message={message}
      title={questionContent.title}
      subtitle={questionContent.subtitle}
      actions={
        isValidationQuestionsConversation
          ? VALIDATION_FREE_FORM_QUESTION_ACTIONS
          : FREE_FORM_QUESTION_ACTIONS
      }
      disabledActions={{
        [MessageResponseAction.Dismiss]: dictationBusy,
        [MessageResponseAction.Submit]: !trimmedAnswer || dictationBusy,
      }}
      actionAccessory={
        audioTranscriptionEnabled ? (
          <HouseholdChatDictationControl
            dictationTarget="response"
            disabled={isSubmitting}
            onStateChange={setDictationState}
            onTranscript={appendTranscript}
          />
        ) : undefined
      }
      onRespond={handleRespond}
      respondedVerbs={FREE_FORM_QUESTION_RESPONDED_VERBS}
    >
      {message.status === MessageStatus.Pending ? (
        <TextInput
          id={`free-form-question-${message.id}`}
          autoFocus={isValidationQuestionsConversation}
          label="Response"
          hideLabel
          placeholder="Type your response..."
          value={answer}
          multiline
          minRows={1}
          disabled={isSubmitting || dictationBusy}
          onChange={(event) => setAnswer(event.target.value)}
          onKeyDown={handleKeyDown}
          sx={getInteractiveMessageInputSx(COLORS.GRAY[200])}
        />
      ) : (
        response?.responseAction === MessageResponseAction.Submit && (
          <Typography
            variant="title-xs"
            sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
          >
            {response.content}
          </Typography>
        )
      )}
    </InteractiveMessageCard>
  );
}
